import { Route, Routes, useLocation, useNavigate } from "react-router";
import SellerLayout from "../layouts/SellerLayout";
import React, { useEffect, useRef, useState } from "react";
import UserLayout from "../layouts/UserLayout";
import { axiosPrivate, CustomAxiosRequestConfig } from "../api/axios";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import LocationProvider from "./LocationProvider";
import DashboardLayout from "../layouts/DashboardLayout";
import UserDashboardLayout from "../layouts/UserDashboardLayout";
import { UserContextProvider } from "./UserContextProvider";
import { StoreContextProvider } from "../context/StoreContext";
import { CreditorContextProvider } from "../context/CreditorContext";
import AdminDashboardLayout from "../layouts/AdminDashboardLayout";
import { AUTH_JWTOKEN_REFRESH, AUTH_JWTOKEN_VERIFY } from "../api/apiClient";
import Lottie from "react-lottie";
import animationData from "../jsons/LoadingBg.json";
import CreditorDashboardLayout from "../layouts/CreditorDashboardLayout";
import NotFound from "../pages/404/NotFound";
import { Bounce, toast } from "react-toastify";
import Vam30Users from "../pages/Vam30Users/Vam30Users";
import PrivateRoute from "./PrivateRoute";
import { Link } from "react-router-dom";
import { DraggedItemsProvider } from "../context/DraggedItemsContext";

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  renderer: "svg",
};

const UserOrdersDashboard = React.lazy(
  () => import("../layouts/UserOrdersDashboard")
);

const BluCreditorDebtsDetails = React.lazy(
  () => import("../pages/creditor-dashboard/BluCreditorDebtsDetails")
);

const TotalParentTableTransaction = React.lazy(
  () => import("../pages/infiniteTable/TotalParentTableTransaction")
);

const OldUserLayout = React.lazy(() => import("../layouts/OldUserLayout"));

const BluCreditorDebts = React.lazy(
  () => import("../pages/creditor-dashboard/BluCreditorDebts")
);

const BluClosedDebts = React.lazy(
  () => import("../pages/creditor-dashboard/BluClosedDebts")
);

const BluInstalmentLegalFile = React.lazy(
  () => import("../pages/creditor-dashboard/BluInstalmentLegalFile")
);

const Calculator = React.lazy(() => import("../pages/dashboard/Calculator"));

const AllLoanWage = React.lazy(
  () => import("../pages/user-dashboard-panel/AllLoanWage")
);

const InfinitTableSendBankGuaranteeCheques = React.lazy(
  () => import("../pages/infiniteTable/InfinitTableSendBankGuaranteeCheques")
);

const ExpertsGuranteesTable = React.lazy(
  () => import("../pages/admin-dashboard/ExpertsGuranteesTable")
);

const InitialStoreTable = React.lazy(
  () => import("../pages/admin-dashboard/InitialStoreTable")
);

const ExcelReportTable = React.lazy(
  () => import("../pages/creditor-dashboard/ExcelReportTable")
);

const ClosedDEbts = React.lazy(
  () => import("../pages/creditor-dashboard/ClosedDEbts")
);

const GuaranteeChequeTagNumber = React.lazy(
  () => import("../components/drawer/GuaranteeChequeTagNumber")
);

const DueChequeExtraditionTable = React.lazy(
  () => import("../pages/dueChequeExtradition/DueChequeExtraditionTable")
);

const CreditorUsers = React.lazy(
  () => import("../pages/CreditorUsers/CreditorUsers")
);

const ChequeExtraditionTable = React.lazy(
  () => import("../pages/chequeExtradition/CequeExtraditionTable")
);

const ChequeTagNumber = React.lazy(
  () => import("../components/drawer/ChequeTagNumber")
);

const ParentTableForFinancial2 = React.lazy(
  () => import("../pages/infiniteTable/ParentTableForFinancial2")
);

const ParentComponent = React.lazy(
  () => import("../pages/infiniteTable/ParentInfiniteExcel")
);
const Reports = React.lazy(() => import("../pages/Reports/MainReports"));

const PerLoanWage = React.lazy(
  () => import("../pages/user-dashboard-panel/PerLoanWage")
);

const PerLoanWageBlu = React.lazy(
  () => import("../pages/user-dashboard-panel/PerLoanWageblu")
);

const PerPaymentsLink = React.lazy(
  () => import("../pages/user-dashboard-panel/PerPaymentsLink")
);

const CreditorDashboard = React.lazy(
  () => import("../pages/creditor-dashboard/CreditorDashboard")
);

const DebtsDetails = React.lazy(
  () => import("../pages/creditor-dashboard/DebtsDetails")
);

const DebtsDetailsForSupervisor = React.lazy(
  () => import("../pages/admin-dashboard/DebtsDetailsForSupervisor")
);

const ClearedOrdersReport = React.lazy(
  () => import("../pages/Reports/ClearedOrdersReport")
);

const InfinitTableSendBankCheques = React.lazy(
  () => import("../pages/infiniteTable/InfinitTableSendBankCheques")
);

const ReceiveFileReport = React.lazy(
  () => import("../pages/creditor-dashboard/ReceiveFileReport")
);

const ReceiveFileTable = React.lazy(
  () => import("../pages/creditor-dashboard/ReceiveFileTable")
);

const Settlement = React.lazy(
  () => import("../pages/creditor-dashboard/settlement/Settlement")
);

const NestedTable = React.lazy(() => import("./NestedTable"));

const NestedTableSam = React.lazy(() => import("./NestedTableSam"));

const UsersDetails = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-users/user-details/UsersDetails"
    )
);

const StoresTable = React.lazy(
  () => import("../pages/admin-dashboard/admin-tabs/users/StoresTable")
);

const OrdersDashboard = React.lazy(() => import("../layouts/OrdersDashboard"));

const SettlementsDashboard = React.lazy(
  () => import("../pages/dashboard/settlements/Settlements")
);

const OrderDetailsStore = React.lazy(
  () => import("../pages/dashboard/OrderDetailsStore")
);

const AnnouncementsStore = React.lazy(
  () => import("../pages/dashboard/AnnouncementsStore")
);

const AnnouncementsUser = React.lazy(
  () => import("../pages/dashboard/AnnouncementsUser")
);

const AnnouncementsAdmin = React.lazy(
  () => import("../pages/admin-dashboard/Announcements")
);

const CreditorCheques = React.lazy(
  () => import("../pages/creditor-dashboard/CreditorCheques")
);

const CreditorPayCheques = React.lazy(
  () => import("../pages/creditor-dashboard/CreditorPayCheques")
);

const SellerDashboardOrderRegisterPage = React.lazy(
  () =>
    import(
      "../pages/seller-dashboard-register/SellerDashboardOrderRegisterPage"
    )
);

const SellerDashboardOrderSendDocumentPage = React.lazy(
  () =>
    import(
      "../pages/seller-dashboard-register/SellerDashboardOrderSendDocumentPage"
    )
);

const StoreDashboard = React.lazy(
  () => import("../pages/dashboard/StoreDashboard")
);

const AdminDashboard = React.lazy(
  () => import("../pages/admin-dashboard/AdminDashboard")
);

const InfiniteTableFinancial = React.lazy(
  () => import("../pages/infiniteTable/InfiniteTableFinancial")
);

const ParentTableForFinancial = React.lazy(
  () => import("../pages/infiniteTable/ParentTableForFinancial")
);

const AdminChequesExcel = React.lazy(
  () => import("../pages/creditor-dashboard/AdminChequesExcel")
);

const ParentTableTransaction = React.lazy(
  () => import("../pages/infiniteTable/ParentTableTransaction")
);

const AdminContract = React.lazy(
  () => import("../pages/admin-dashboard/contracts")
);

const PardonFee = React.lazy(() => import("../pages/infiniteTable/PardonFee"));

const FinancialOrders = React.lazy(
  () => import("../pages/creditor-dashboard/FinancialOrders")
);

const CreditorDebts = React.lazy(
  () => import("../pages/creditor-dashboard/CreditorDebts")
);

const CreditorDebtsForSupervisor = React.lazy(
  () => import("../pages/admin-dashboard/CreditorDebtsForSupervisor")
);
const AdminMessage = React.lazy(
  () =>
    import("../pages/admin-dashboard/admin-dashboard-basicInfo/message/Message")
);

const CreditorSendBank = React.lazy(
  () => import("../pages/creditor-dashboard/CreditorSendBank")
);

const AdminDashboardSetting = React.lazy(
  () => import("../pages/admin-dashboard/AdminDashboardSetting")
);

const AdminDashboardTimeWork = React.lazy(
  () => import("../pages/admin-dashboard/settings/Settings")
);

const ApplierTable = React.lazy(
  () => import("../pages/admin-dashboard/admin-tabs/users/applier/ApplierTable")
);

const AdminProductGroup = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/related-definitions/product-group/ProductGroup"
    )
);

const Banks = React.lazy(
  () => import("../pages/admin-dashboard/admin-dashboard-basicInfo/banks/Banks")
);

const ProvinceCity = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/province-city/ProviceCity"
    )
);

const Loans = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/loans-credit/loans/Loans"
    )
);

const StageShop = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/loans-credit/stage-shop/StageShop"
    )
);

const Credit = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/loans-credit/credit/Credit"
    )
);
const StoreGroups = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/loans-credit/storegroup"
    )
);

const AdminOrder = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/related-definitions/order/Order"
    )
);

const AdminLicenceActivity = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/related-definitions/licence-activity/LicenceActivity"
    )
);

const AdminBrands = React.lazy(
  () =>
    import(
      "../pages/admin-dashboard/admin-dashboard-basicInfo/related-definitions/brands/Brand"
    )
);

const CreditorOrderTable = React.lazy(
  () => import("../pages/creditor-dashboard/applier/ApplierTable")
);

const ApplierReport = React.lazy(
  () =>
    import("../pages/admin-dashboard/admin-tabs/users/applier/ApplierReport")
);

const CreditorOrderDetail = React.lazy(
  () => import("../pages/creditor-dashboard/applier/ApplierReport")
);

const CreditCalculatorUser = React.lazy(
  () => import("../pages/user-dashboard-panel/CreditCalculatorUser")
);

const Profile = React.lazy(() => import("../pages/dashboard/Profile"));

const StoreContracts = React.lazy(
  () => import("../pages/dashboard/StoreContracts")
);

const UserDashboardPanel = React.lazy(
  () => import("../pages/user-dashboard-panel/UserDashboardPanel")
);

const UserDashboardProfile = React.lazy(
  () => import("../pages/user-dashboard-panel/UserDashboardProfile")
);

const UserDashboardOrders = React.lazy(
  () => import("../pages/user-dashboard-panel/UserDashboardOrders")
);

const UserDashboardInstalments = React.lazy(
  () => import("../pages/user-dashboard-panel/UserDashboardInstalments")
);

const UserDashboardServices = React.lazy(
  () => import("../pages/user-dashboard-panel/UserDashboardServices")
);

const AdminDashboardUsers = React.lazy(
  () =>
    import(
      "../../src/pages/admin-dashboard/admin-dashboard-users/AdminDashboardUsers"
    )
);

const AdminDashboardStore = React.lazy(
  () =>
    import(
      "../../src/pages/admin-dashboard/admin-dashboard-store/AdminDashboardStore"
    )
);

const CostumerArrears = React.lazy(() => import("../layouts/CostumerArrears"));

const StoreDeatails = React.lazy(
  () => import("../pages/admin-dashboard/admin-dashboard-store/StoreDeatails")
);

const CommonQuestion = React.lazy(
  () => import("../pages/admin-dashboard/CommonQuestion/CommonQuestion")
);
const DailyQuestion = React.lazy(
  () => import("../pages/admin-dashboard/DailyQuestion/DailyQuestion")
);

const CommentsConfirm = React.lazy(
  () => import("../pages/admin-dashboard/admin-dashboard-comments/ContentSite")
);

const Blog = React.lazy(
  () => import("../pages/admin-dashboard/admin-dashboard-blog/Blog")
);
const BiPage = React.lazy(() => import("../pages/admin-dashboard/BiPage"));

const Fallback = () => (
  <div className="min-h-screen flex items-center justify-center min-w-full">
    <Lottie options={defaultOptions} height={100} width={100} />
  </div>
);

export default function RoutesWithAnimation({
  enteredUser,
  setEnteredUser,
  setIsModalOpen,
  isModalOpen,
}: any) {
  const location = useLocation();
  const access_token = localStorage.getItem("access_token");
  const refresh = localStorage.getItem("refresh");
  const [formNo, setFormNo] = useState<number>(1);

  const navigate = useNavigate();

  const verifyToken = async () => {
    if (access_token) {
      try {
        const response = await axiosPrivate.post(
          AUTH_JWTOKEN_VERIFY,
          { token: access_token },
          {
            headers: {
              // prettier-ignore
              "accept": "application/json",
              // prettier-ignore
              "Authorization": `Bearer ${access_token}`,
            },
            withCredentials: true,

            globalErrorHandler: false,
          } as CustomAxiosRequestConfig
        );
        if (response?.status >= 200 && response?.status < 300) {
          setEnteredUser(
            localStorage.getItem("user_state") === "REGISTERED" ||
              localStorage.getItem("user_state") === "VERIFIED"
              ? localStorage.getItem("access_token")
              : null
          );
        }
      } catch (error) {
        const responseStatus: any = (error as any).response?.status;
        if (responseStatus === 401) {
          document.cookie.split(";").forEach((cookie) => {
            const name = cookie.split("=")[0].trim();
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          if (refresh) refreshHandler();
          else {
            toast.error("لطفا مجددا وارد حساب کاربری خود شوید.");
            setEnteredUser(null);
            localStorage.clear();
            document.cookie.split(";").forEach((cookie) => {
              const name = cookie.split("=")[0].trim();
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            });
            navigate("/");
          }
        }

        if (responseStatus >= 500) {
          toast.error("سامانه در حال بروزرسانی است");
        }
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
      }
    }
  };

  async function refreshHandler() {
    try {
      const response = await axiosPrivate.post(
        AUTH_JWTOKEN_REFRESH,
        { refresh: refresh },
        {
          headers: {
            // prettier-ignore
            "accept": "application/json",
            // prettier-ignore
          },
          withCredentials: true,

          globalErrorHandler: false,
        } as CustomAxiosRequestConfig
      );
      if (response?.status >= 200 && response?.status < 300) {
        setEnteredUser(
          localStorage.getItem("user_state") === "REGISTERED" ||
            localStorage.getItem("user_state") === "VERIFIED"
            ? localStorage.getItem("access_token")
            : null
        );
        localStorage.setItem("access_token", response?.data?.data?.access);
        document.cookie = `access_token=${response?.data?.data?.access}; path=/; max-age=86400`;
        localStorage.setItem("refresh", response?.data?.data?.refresh);
        window.location.reload();
      }
    } catch (error) {
      const responseStatus: any = (error as any).response?.status;
      if (responseStatus === 401) {
        toast.error("لطفا مجددا وارد حساب کاربری خود شوید.");
        setEnteredUser(null);
        localStorage.clear();
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        navigate("/");
      }

      if (responseStatus >= 500) {
        toast.error("سامانه در حال بروزرسانی است");
      }
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
    }
  }

  const old_access_token = localStorage.getItem("old_access_token");

  useEffect(() => {
    verifyToken();
  }, []);

  useEffect(() => {
    old_access_token
      ? toast.info("شما در پنل کاربر هستید", {
          position: "bottom-left",
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          transition: Bounce,
        })
      : toast.dismiss();
  }, [old_access_token, window.location.pathname]);

  useEffect(() => {
    const isLocalhost = window.location.hostname === "localhost";

    if (!isLocalhost && window.location.pathname === "/") {
      window.location.href = "https://vam30.com";
    } else if (
      window.location.pathname === "/home/faq" ||
      window.location.pathname === "/more-question-list"
    ) {
      window.location.href = "https://vam30.com/faq";
    } else if (
      window.location.pathname === "/fa" ||
      window.location.pathname === "/Home/Start" ||
      window.location.pathname === "/Home" ||
      window.location.pathname === "/mobile-register-info" ||
      window.location.pathname === "/home/SelectedStores" ||
      window.location.pathname === "/Login"
    ) {
      window.location.href = "https://vam30.com/";
    } else if (window.location.pathname === "/fa/shops") {
      window.location.href = "https://vam30.com/shops";
    } else if (window.location.pathname === "/user-auth") {
      window.location.href = "https://vam30.com/fa/user-auth";
    }
  }, [window.location]);

  const newPageRef2 = useRef<HTMLDivElement>(null);

  const queryClient = new QueryClient();

  return (
    <LocationProvider>
      <AnimatePresence>
        <CreditorContextProvider>
          <UserContextProvider>
            <StoreContextProvider>
              <QueryClientProvider client={queryClient}>
                <Routes location={location}>
                  <Route
                    path="/fa/seller-auth"
                    element={
                      <PrivateRoute
                        setEnteredUser={setEnteredUser}
                        setIsModalOpen={setIsModalOpen}
                      >
                        <SellerLayout
                          setFormNo={setFormNo}
                          formNo={formNo}
                          enteredUser={enteredUser}
                          setEnteredUser={setEnteredUser}
                          newPageRef2={newPageRef2}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/fa/user-auth"
                    element={
                      <PrivateRoute
                        setEnteredUser={setEnteredUser}
                        setIsModalOpen={setIsModalOpen}
                      >
                        <UserLayout
                          enteredUser={enteredUser}
                          setEnteredUser={setEnteredUser}
                          newPageRef2={newPageRef2}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/fa/old-user-panel"
                    element={
                      <OldUserLayout
                        enteredUser={enteredUser}
                        setEnteredUser={setEnteredUser}
                        newPageRef2={newPageRef2}
                      />
                    }
                  />
                  <Route
                    path="/fa/user-panel"
                    element={
                      <UserDashboardLayout
                        enteredUser={enteredUser}
                        setEnteredUser={setEnteredUser}
                      />
                    }
                  >
                    <Route
                      index
                      path="/fa/user-panel"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserDashboardPanel />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/profile"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserDashboardProfile />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/notifUser"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AnnouncementsUser />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/orders"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserDashboardOrders />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/instalments"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserDashboardInstalments />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/services"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserDashboardServices />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/user-panel/calculator"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditCalculatorUser />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/user-panel/loan-wage/:trackingCode"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PerLoanWage />
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/user-panel/instalment-debts-payments/:trackingCode"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PerLoanWageBlu />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/user-panel/debts-payments/:trackingCode"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PerPaymentsLink />
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/user-panel/total-debts/:trackingCode"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <AllLoanWage />
                        </React.Suspense>
                      }
                    />
                  </Route>
                  <Route path="/fa/dashboard" element={<DashboardLayout />}>
                    <Route
                      path="/fa/dashboard"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StoreDashboard />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/notifStore"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AnnouncementsStore />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/orders"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <OrdersDashboard
                              setFormNo={setFormNo}
                              formNo={formNo}
                            />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/user-register"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UserOrdersDashboard />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/settlements"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <SettlementsDashboard />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />{" "}
                    <Route
                      index
                      path="/fa/dashboard/orders-details/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <OrderDetailsStore />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/order-register/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <SellerDashboardOrderRegisterPage
                              setFormNo={setFormNo}
                              formNo={formNo}
                            />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/costumer-arrears"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CostumerArrears />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/order-register"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <SellerDashboardOrderRegisterPage
                              setFormNo={setFormNo}
                              formNo={formNo}
                            />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/order-send-documents/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <SellerDashboardOrderSendDocumentPage
                              setFormNo={setFormNo}
                              formNo={formNo}
                            />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/profile"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Profile />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/calculator"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Calculator />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/dashboard/contracts"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StoreContracts />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                  </Route>
                  <Route
                    path="/fa/admin-dashboard"
                    element={<AdminDashboardLayout />}
                  >
                    <Route
                      path="/fa/admin-dashboard"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DraggedItemsProvider>
                              <AdminDashboard />
                            </DraggedItemsProvider>
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/contracts"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminContract />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/users/"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <AdminDashboardUsers
                                setFormNo={setFormNo}
                                formNo={formNo}
                              />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/users/details/:user_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <UsersDetails />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/reports/"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <Reports />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/initial-store-table/"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <InitialStoreTable />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/reports/:reportType"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ClearedOrdersReport />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/store"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <StoresTable />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/store/details/:store_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <QueryClientProvider client={queryClient}>
                            <PrivateRoute
                              setEnteredUser={setEnteredUser}
                              setIsModalOpen={setIsModalOpen}
                            >
                              <AdminDashboardStore />
                            </PrivateRoute>
                          </QueryClientProvider>
                        </React.Suspense>
                      }
                    />

                    <Route
                      index
                      path="/fa/admin-dashboard/user/creditor-users"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorUsers />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/user/vam30-users"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Vam30Users />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/Store-Info/:store_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StoreDeatails showFourBtn={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/User-Info/:user_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UsersDetails backtoList={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/setting"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminDashboardSetting />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/worktime"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminDashboardTimeWork />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/applier"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ApplierTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/store-definitions/product-group"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminProductGroup />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/banks"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Banks />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/province"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ProvinceCity />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/loans"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Loans />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/stageshop"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StageShop />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/credit"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Credit />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/storegroup"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StoreGroups />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/store-definitions/order"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminOrder />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/licence-activity/licence-activity"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminLicenceActivity />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      index
                      path="/fa/admin-dashboard/base-info/store-definitions"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminBrands />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/notifUser"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AnnouncementsAdmin />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/applier/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ApplierReport />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/common-questions"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CommonQuestion />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/daily-questions"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DailyQuestion />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/comments"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CommentsConfirm />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/admin-dashboard/blog"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Blog />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/debts"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorDebtsForSupervisor />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/closed-debts/"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ClosedDEbts redirectTo={true} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/message"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminMessage />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/admin-dashboard/debts/:debt_file_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DebtsDetailsForSupervisor />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      index
                      path="/fa/admin-dashboard/bi-panel"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <BiPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/admin-dashboard/excel-reports-table"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ExcelReportTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                  </Route>
                  <Route
                    path="/fa/creditor-dashboard"
                    element={<CreditorDashboardLayout />}
                  >
                    <Route
                      path="/fa/creditor-dashboard/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorDashboard />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/cheques-financial/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <InfinitTableSendBankCheques />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/creditor-dashboard/blu-closed-debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <BluClosedDebts redirect={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/creditor-dashboard/guarantee-cheques-financial/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <InfinitTableSendBankGuaranteeCheques />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/extradition-table/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ChequeExtraditionTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/orders-details/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <OrderDetailsStore />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/experts-gurantees/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ExpertsGuranteesTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/due-extradition-table/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DueChequeExtraditionTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/reports/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Reports />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/reports/:creditor_uid/:reportType"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ClearedOrdersReport />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/applier/:creditor_uid/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorOrderDetail />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/orders/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorOrderTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/creditor-dashboard/cheques-problem/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <InfiniteTableFinancial />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/cheques-excel/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ParentTableForFinancial />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/guarantee-cheques-states/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ParentTableForFinancial2 />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/cheques-return-bank"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AdminChequesExcel />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/returned-bank-cheques/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ParentComponent />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/financial-orders"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <FinancialOrders />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/pardon-fee"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <PardonFee />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/transaction/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ParentTableTransaction />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/paycheques-details/:debt_file_uid/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DebtsDetails customeHeader="/fa/creditor-dashboard/paycheques" />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorDebts />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/blu-instalment-debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <BluCreditorDebts />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/blu-instalment-debts/:debt_file_uid/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <BluCreditorDebtsDetails />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/creditor-dashboard/blu-instalment-legal-file/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <BluInstalmentLegalFile />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />

                    <Route
                      path="/fa/creditor-dashboard/send-bank/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorSendBank />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/total_debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <TotalParentTableTransaction />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/closed-debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ClosedDEbts redirectTo={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/tag-number/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ChequeTagNumber />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/guarantee-tag-number/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <GuaranteeChequeTagNumber />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/debts/:creditor_uid/:debt_file_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <DebtsDetails />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/cheques/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorCheques />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/paycheques/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <CreditorPayCheques />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/excel-reports-table/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ExcelReportTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/store-debts/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <NestedTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/store-debts-sam/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <NestedTableSam />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="/fa/creditor-dashboard/notifUser/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <AnnouncementsUser />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/Store-Info/:store_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <StoreDeatails showFourBtn={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/User-Info/:user_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <UsersDetails backtoList={false} />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/applier"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ApplierReport />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/applier/stores/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ReceiveFileTable />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/settlement/stores/:creditor_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <Settlement />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/applier/stores/:creditor_uid/:order_uid"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <ReceiveFileReport />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                    <Route
                      index
                      path="/fa/creditor-dashboard/applier/Guarantor"
                      element={
                        <React.Suspense fallback={<Fallback />}>
                          <PrivateRoute
                            setEnteredUser={setEnteredUser}
                            setIsModalOpen={setIsModalOpen}
                          >
                            <InfinitTableSendBankCheques />
                          </PrivateRoute>
                        </React.Suspense>
                      }
                    />
                  </Route>
                  <Route
                    path="/home/faq"
                    element={<Link to="/faq" replace />}
                  />
                  <Route
                    path="/more-question-list"
                    element={<Link to="/faq" replace />}
                  />
                  <Route path="/fa" element={<Link to="/" replace />} />
                  <Route path="/Home/Start" element={<Link to="/" replace />} />
                  <Route path="/Home" element={<Link to="/" replace />} />
                  <Route
                    path="/mobile-register-info"
                    element={<Link to="/" replace />}
                  />
                  <Route path="/Login" element={<Link to="/" replace />} />
                  <Route
                    path="/fa/shops"
                    element={<Link to="/vam30.com/shops" replace />}
                  />

                  <Route
                    path="/user-auth"
                    element={<Link to="/fa/user-auth" replace />}
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </QueryClientProvider>
            </StoreContextProvider>
          </UserContextProvider>
        </CreditorContextProvider>
      </AnimatePresence>
    </LocationProvider>
  );
}
